/**
 * Badge.tsx — Componente de etiqueta de estado del design system
 *
 * Patrón: CVA puro + Radix Slot (asChild) + Tailwind + Lucide.
 * Sigue la referencia canónica de Button.tsx (ADR-025).
 *
 * Por qué sin primitivo Radix de comportamiento:
 *   Badge es un elemento presentacional (etiqueta de estado/conteo), no
 *   interactivo. No requiere ARIA complejo. Quality gate Día 2 §7 confirma
 *   CVA puro. Radix Slot se usa SOLO para asChild (no añade ARIA).
 *
 * Variantes semánticas: neutral | success | warning | danger | info
 *   Mapeadas a tokens cumple-* con patrón soft/tinted: fondo /10, borde /20.
 *   Fondos tenues via opacidad Tailwind (ej. bg-cumple-success-600/10) para
 *   evitar hardcodear hex sin crear nuevos tokens.
 *
 * [WCAG 1.4.1 — Color no único canal]
 *   El contenido textual del badge DEBE nombrar el estado ("Crítico", "OK").
 *   El color/variante es refuerzo visual, no el único canal informativo.
 *   Badge solo-ícono requiere aria-label explícito del consumidor.
 *
 * [DEUDA-B-E2E-BADGE-CONTRAST — Prioridad media]
 *   axe-core/jsdom no computa contraste real (getComputedStyle limitado).
 *   El contraste de fondos tintados sobre blanco de página debe validarse
 *   manualmente o en E2E Playwright. Análogo a DEUDA-B-E2E-IN10.
 *
 * Elemento raíz: <span> (inline, no rompe flujo de texto).
 */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './utils';

// ---------------------------------------------------------------------------
// Variantes CVA
// ---------------------------------------------------------------------------

const badgeVariants = cva(
  // Base: inline-flex, redondeado completo, fuente medium, no rompe en línea
  [
    'inline-flex items-center gap-1',
    'rounded-full font-medium whitespace-nowrap select-none border',
  ],
  {
    variants: {
      variant: {
        /**
         * neutral — Estado sin semántica especial o desconocido.
         * Texto gray-700 sobre fondo gray-100 con borde gray-200.
         */
        neutral: ['text-cumple-gray-700', 'bg-cumple-gray-100', 'border-cumple-gray-200'],
        /**
         * success — Estado exitoso, aprobado, conforme.
         * Texto success-600 sobre fondo tintado /10 con borde /20.
         */
        success: [
          'text-cumple-success-600',
          'bg-cumple-success-600/10',
          'border-cumple-success-600/20',
        ],
        /**
         * warning — Estado de alerta, revisión pendiente, en riesgo.
         */
        warning: [
          'text-cumple-warning-500',
          'bg-cumple-warning-500/10',
          'border-cumple-warning-500/20',
        ],
        /**
         * danger — Estado crítico, no conforme, error.
         * Texto danger-700 (no -500) para mayor contraste sobre fondo claro.
         */
        danger: [
          'text-cumple-danger-700',
          'bg-cumple-danger-500/10',
          'border-cumple-danger-500/20',
        ],
        /**
         * info — Estado informativo. Usa token accent (azul) como proxy de info.
         */
        info: ['text-cumple-accent-600', 'bg-cumple-accent-500/10', 'border-cumple-accent-500/20'],
      },
      size: {
        /** sm — Texto xs, padding compacto, ícono 12px */
        sm: 'text-xs px-2 py-0.5 [&>svg]:h-3 [&>svg]:w-3',
        /** md — Texto sm, padding estándar, ícono 14px */
        md: 'text-sm px-2.5 py-0.5 [&>svg]:h-3.5 [&>svg]:w-3.5',
      },
    },
    defaultVariants: {
      variant: 'neutral',
      size: 'md',
    },
  },
);

// ---------------------------------------------------------------------------
// Props del componente
// ---------------------------------------------------------------------------

export interface BadgeProps
  extends React.HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badgeVariants> {
  /**
   * asChild — Cuando es true, fusiona las props con el único hijo React.
   * Útil para renderizar como <a> (badge filtro removible) o <button>.
   * Patrón Radix Slot: igual que Button.tsx.
   *
   * @example Badge como enlace:
   *   <Badge asChild variant="info">
   *     <a href="/filtro">ISO 27001</a>
   *   </Badge>
   */
  asChild?: boolean;
  /**
   * leadingIcon — Ícono Lucide opcional a la izquierda del texto.
   * Por ADR-027 §plan Día 5: Dot o Circle para status.
   * El componente envuelve el nodo en un <span aria-hidden="true"> (decorativo;
   * el texto del badge ya comunica el estado).
   *
   * @example
   *   import { Circle } from 'lucide-react';
   *   <Badge variant="success" leadingIcon={<Circle />}>Aprobado</Badge>
   */
  leadingIcon?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Componente
// ---------------------------------------------------------------------------

/**
 * Badge — Etiqueta de estado o conteo inline.
 *
 * El badge es semánticamente texto (<span>). No lleva `role`. El contenido
 * textual ("Crítico", "3 hallazgos") es leído por AT como parte del flujo.
 *
 * WCAG 1.4.1: el estado NO se comunica solo por color. El texto es obligatorio
 * para badgdes con contenido. Para badges solo-ícono, pasar `aria-label`.
 *
 * No aplica min-h-[44px]: Badge es presentacional. Si se usa como control
 * interactivo via asChild, el consumidor es responsable del área táctil.
 */
const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  ({ className, variant, size, asChild = false, leadingIcon, children, ...props }, ref) => {
    const Comp = asChild ? Slot : 'span';

    // Cuando asChild=true, Radix Slot requiere exactamente un hijo React.
    // Con leadingIcon + children habría dos nodos. Se envuelven en un Fragment
    // solo cuando ambos están presentes, pero Slot no acepta Fragment...
    // Solución: cuando asChild=true ignorar leadingIcon (el consumidor controla
    // el contenido de su propio elemento) o renderizarlos dentro del hijo.
    // El patrón Button con asChild también omite los estados internos.
    // Badge con asChild delega el layout al elemento hijo.
    if (asChild) {
      return (
        <Comp ref={ref} className={cn(badgeVariants({ variant, size }), className)} {...props}>
          {children}
        </Comp>
      );
    }

    return (
      <Comp
        ref={ref}
        // role="status" permite aria-label en <span> (WCAG 4.1.2 / axe aria-prohibited-attr).
        // Se aplica solo cuando hay aria-label (badge solo-ícono) para no añadir semántica
        // innecesaria a badges con texto. El consumidor puede sobreescribir via props.
        role={props['aria-label'] && !children ? 'status' : undefined}
        className={cn(badgeVariants({ variant, size }), className)}
        {...props}
      >
        {leadingIcon && (
          // El ícono es decorativo: el texto ya comunica el estado.
          // aria-hidden forzado independientemente de lo que pase el consumidor.
          <span aria-hidden="true">{leadingIcon}</span>
        )}
        {children}
      </Comp>
    );
  },
);

Badge.displayName = 'Badge';

export { Badge, badgeVariants };
